import { globalVar } from 'app/infrastructure/universal/runtime';

/**
 * Populated appsettings script output by the asp.net core tag helper
 */
const applicationSettingsKey = '__application__';

/**
 * Environment variables on the global __application__ object
 * populated by the server environment
 */
const SETTING_NAMES = {
  // TODO: These keys shouldn't need to be here. Temporary workaround until we can have
  // different variables per application in our deployments.
  AnonymousApplicationUrl: 'anonymousApplicationUrl',
  AnonymousApiBaseUrl: 'anonymousApiBaseUrl',
  GoogleRecaptchaSiteKey: 'googleRecaptchaSiteKey',

  AcceptedLanguage: 'acceptLanguage',
  HostingPath: 'hostingPath',
  ApiBaseUrl: 'apiBaseUrl',
  AdminApiBaseUrl: 'adminApiBaseUrl',
  NotificationsApiBaseUrl: 'notificationsApiBaseUrl',
  LogoutLandingPage: 'logoutLandingPage',
  VeracityProfileLink: 'veracityProfileLink',
  VeracityPolicyCheckLink: 'veracityPolicyCheckLink',
  InviteVeracityUserLink: 'inviteVeracityUserLink',
  VeracityCompanyAdministrationLink: 'veracityCompanyAdministrationLink',
  ApplicationUrl: 'applicationUrl',
  ClientDebugLog: 'clientDebugLog',
  ComponentDebug: 'componentDebug',
  OidcServiceUrl: 'oidcServiceUrl',
  OidcClientId: 'oidcClientId',
  OidcScope: 'oidcScope',
  OidcDefaultProviderId: 'oidcDefaultProviderId',
  OverriddenOidcDefaultProviderId: 'overriddenOidcDefaultProviderId',
  OidcDontLoadUserInfo: 'oidcDontLoadUserInfo',
  Translations: 'translations',
  Integrations: 'integrations',
  UseLocalStorageTokenCache: 'useLocalStorageTokenCache',
  FeatureToggle: 'featureToggle',
  WebPush: 'webPush'
};

class AppSettingHandler {
  public init() {
    // Only relevant for tests, since they need to initialize the global object manually
    globalVar(applicationSettingsKey, {});
  }

  private get appSettings() {
    return globalVar(applicationSettingsKey) || {};
  }

  get AcceptedLanguage() {
    return this.appSettings[SETTING_NAMES.AcceptedLanguage];
  }

  set AcceptedLanguage(value: string) {
    this.appSettings[SETTING_NAMES.AcceptedLanguage] = value;
  }

  get HostingPath() {
    return this.appSettings[SETTING_NAMES.HostingPath];
  }

  get ApiBaseUrl() {
    return this.appSettings[SETTING_NAMES.ApiBaseUrl];
  }

  set ApiBaseUrl(value: string) {
    this.appSettings[SETTING_NAMES.ApiBaseUrl] = value;
  }

  get AdminApiBaseUrl() {
    return this.appSettings[SETTING_NAMES.AdminApiBaseUrl];
  }

  get NotificationsApiBaseUrl() {
    return this.appSettings[SETTING_NAMES.NotificationsApiBaseUrl];
  }

  get LogoutLandingPage() {
    return this.appSettings[SETTING_NAMES.LogoutLandingPage];
  }

  get VeracityProfileLink() {
    return this.appSettings[SETTING_NAMES.VeracityProfileLink];
  }

  get VeracityPolicyCheckLink() {
    return this.appSettings[SETTING_NAMES.VeracityPolicyCheckLink];
  }

  get InviteVeracityUserLink() {
    return this.appSettings[SETTING_NAMES.InviteVeracityUserLink];
  }

  get VeracityCompanyAdministrationLink() {
    return this.appSettings[SETTING_NAMES.VeracityCompanyAdministrationLink];
  }

  get ApplicationUrl() {
    return this.appSettings[SETTING_NAMES.ApplicationUrl];
  }

  set ApplicationUrl(value: string) {
    this.appSettings[SETTING_NAMES.ApplicationUrl] = value;
  }

  get ClientDebugLog(): boolean {
    return !!this.appSettings[SETTING_NAMES.ClientDebugLog];
  }

  set ClientDebugLog(value: boolean) {
    this.appSettings[SETTING_NAMES.ClientDebugLog] = value;
  }

  get ComponentDebug(): boolean {
    return !!this.appSettings[SETTING_NAMES.ComponentDebug];
  }

  set ComponentDebug(value: boolean) {
    this.appSettings[SETTING_NAMES.ComponentDebug] = value;
  }

  get OidcServiceUrl() {
    return this.appSettings[SETTING_NAMES.OidcServiceUrl];
  }

  get OidcClientId() {
    return this.appSettings[SETTING_NAMES.OidcClientId];
  }

  get OidcScope() {
    return this.appSettings[SETTING_NAMES.OidcScope];
  }

  get OidcDefaultProviderId() {
    const storage: Storage = globalVar('localStorage');
    const overriddenOidcDefaultProviderId = storage.getItem('SL.overriddenOidcDefaultProviderId');

    return overriddenOidcDefaultProviderId || this.appSettings[SETTING_NAMES.OidcDefaultProviderId];
  }

  get OverriddenOidcDefaultProviderId() {
    return this.appSettings[SETTING_NAMES.OverriddenOidcDefaultProviderId];
  }

  get OidcDontLoadUserInfo(): boolean {
    return !!this.appSettings[SETTING_NAMES.OidcDontLoadUserInfo];
  }

  get Translations() {
    return this.appSettings[SETTING_NAMES.Translations];
  }

  get Integrations() {
    return this.appSettings[SETTING_NAMES.Integrations];
  }

  get UseLocalStorageTokenCache() {
    return this.appSettings[SETTING_NAMES.UseLocalStorageTokenCache];
  }

  get FeatureToggle() {
    return this.appSettings[SETTING_NAMES.FeatureToggle];
  }

  get WebPush() {
    return this.appSettings[SETTING_NAMES.WebPush];
  }

  get AnonymousApplicationUrl() {
    return this.appSettings[SETTING_NAMES.AnonymousApplicationUrl];
  }

  get AnonymousApiBaseUrl() {
    return this.appSettings[SETTING_NAMES.AnonymousApiBaseUrl];
  }

  get GoogleRecaptchaSiteKey() {
    return this.appSettings[SETTING_NAMES.GoogleRecaptchaSiteKey];
  }
}

export const AppSetting = new AppSettingHandler();

export const persistOverriddenSettings = () => {
  const storage: Storage = globalVar('localStorage');
  if(AppSetting.OverriddenOidcDefaultProviderId === 'clr') {
    storage.removeItem('SL.overriddenOidcDefaultProviderId');
  }
  else {
    AppSetting.OverriddenOidcDefaultProviderId && 
    storage.setItem('SL.overriddenOidcDefaultProviderId', AppSetting.OverriddenOidcDefaultProviderId);
  }
};
